/* Break-Even Analysis Calculator Logic — Math, Charts & CSV Export */

let beDonutChart = null;
let beGrowthChart = null;

function getBeInputs() {
  const getVal = (id, def = 0) => {
    const el = document.getElementById(id);
    if (!el) return def;
    const v = +el.value;
    return isNaN(v) ? def : v;
  };

  return {
    fixed: getVal('beFixed', 500000),
    price: getVal('bePrice', 1000),
    variable: getVal('beVariable', 400),
    expectedUnits: getVal('beExpectedUnits', 1500),
    recurringIncome: getVal('beRecurringIncome', 100000),
    growthRate: getVal('beGrowthRate', 5),
  };
}

function updateBeUrlParams(params) {
  try {
    const url = new URL(window.location);
    url.searchParams.set('fixed', params.fixed);
    url.searchParams.set('price', params.price);
    url.searchParams.set('variable', params.variable);
    url.searchParams.set('expectedUnits', params.expectedUnits);
    url.searchParams.set('recurringIncome', params.recurringIncome);
    url.searchParams.set('growthRate', params.growthRate);
    window.history.replaceState({}, '', url);
  } catch (e) {}
}

function parseBeUrlParams() {
  const searchParams = new URLSearchParams(window.location.search);
  const getNum = (key) => {
    const val = searchParams.get(key);
    return val !== null ? +val : null;
  };

  return {
    fixed: getNum('fixed'),
    price: getNum('price'),
    variable: getNum('variable'),
    expectedUnits: getNum('expectedUnits'),
    recurringIncome: getNum('recurringIncome'),
    growthRate: getNum('growthRate'),
  };
}

function calculateBe(params) {
  const { fixed, price, variable, expectedUnits, recurringIncome, growthRate } = params;

  const unitContribution = Math.max(0.01, price - variable);
  const pvRatio = price > 0 ? (unitContribution / price) * 100 : 0;

  // Net fixed costs after base recurring income offset
  const netFixedCost = Math.max(0, fixed - recurringIncome);
  const beUnits = unitContribution > 0 ? Math.ceil(netFixedCost / unitContribution) : 0;
  const beRevenue = beUnits * price;

  const mosUnits = Math.max(0, expectedUnits - beUnits);
  const mosPercent = expectedUnits > 0 ? (mosUnits / expectedUnits) * 100 : 0;

  // Build 12-Month Projection Schedule factoring recurring income growth %
  const schedule = [];
  let beMonth = null;

  for (let m = 1; m <= 12; m++) {
    const baseIncome = recurringIncome * Math.pow(1 + growthRate / 100, m - 1);
    const unitRevenue = expectedUnits * price;
    const totalRevenue = baseIncome + unitRevenue;
    const variableCosts = expectedUnits * variable;
    const totalCosts = fixed + variableCosts;
    const profit = totalRevenue - totalCosts;

    if (profit >= 0 && beMonth === null) {
      beMonth = m;
    }

    schedule.push({
      month: m,
      baseIncome: Math.round(baseIncome),
      unitRevenue: Math.round(unitRevenue),
      totalRevenue: Math.round(totalRevenue),
      fixedCosts: Math.round(fixed),
      variableCosts: Math.round(variableCosts),
      totalCosts: Math.round(totalCosts),
      profit: Math.round(profit),
    });
  }

  return {
    fixed: Math.round(fixed),
    price: Math.round(price),
    variable: Math.round(variable),
    expectedUnits: Math.round(expectedUnits),
    recurringIncome: Math.round(recurringIncome),
    growthRate: +growthRate.toFixed(1),
    unitContribution: Math.round(unitContribution),
    pvRatio: +pvRatio.toFixed(1),
    beUnits: Math.round(beUnits),
    beRevenue: Math.round(beRevenue),
    beMonth,
    mosUnits: Math.round(mosUnits),
    mosPercent: +mosPercent.toFixed(1),
    schedule,
  };
}

function updateDisplays(params) {
  const setTxt = (id, txt) => {
    const el = document.getElementById(id);
    if (el) el.textContent = txt;
  };

  setTxt('beFixedDisplay', fmt(params.fixed));
  setTxt('bePriceDisplay', fmt(params.price));
  setTxt('beVariableDisplay', fmt(params.variable));
  setTxt('beExpectedUnitsDisplay', `${params.expectedUnits.toLocaleString()} units`);
  setTxt('beRecurringIncomeDisplay', fmt(params.recurringIncome));
  setTxt('beGrowthRateDisplay', `${params.growthRate}% / mo`);
}

function renderBeResults(result, params) {
  const setTxt = (id, txt) => {
    const el = document.getElementById(id);
    if (el) el.textContent = txt;
  };

  const beStr = result.beMonth ? `Break-Even Month: Mo ${result.beMonth}` : 'Break-Even Month: > 12 Months';
  setTxt('beVerdictHeadline', `BEP: ${result.beUnits.toLocaleString()} Units | ${beStr}`);
  setTxt('beVerdictDetail', `Selling ${params.expectedUnits.toLocaleString()} units with ${fmt(params.recurringIncome)} recurring base income (${params.growthRate}%/mo growth) achieves net monthly profitability in ${result.beMonth ? 'Month ' + result.beMonth : 'over 12 months'}.`);

  setTxt('beRevenueStatValue', fmt(result.beRevenue));
  setTxt('beMarginStatValue', fmt(result.unitContribution));
  setTxt('beUnitsMetricValue', `${result.beUnits.toLocaleString()} Units`);
  setTxt('bePvMetricValue', `${result.pvRatio}%`);
  
  if (params.expectedUnits <= 0) {
    setTxt('beMosMetricValue', '0% (Set Expected Sales)');
  } else if (params.expectedUnits < result.beUnits) {
    setTxt('beMosMetricValue', '0% (Below BEP — Loss)');
  } else if (params.expectedUnits === result.beUnits) {
    setTxt('beMosMetricValue', '0% (At BEP)');
  } else {
    setTxt('beMosMetricValue', `${result.mosPercent}% (${result.mosUnits.toLocaleString()} Units)`);
  }

  renderTable(result.schedule, result.beMonth);

  try {
    if (typeof Chart !== 'undefined') {
      renderDonutChart(params.variable, result.unitContribution, params.price);
      renderGrowthChart(result.schedule);
    }
  } catch (err) {
    console.warn('Break-Even Chart rendering error:', err);
  }
}

function renderDonutChart(variableCost, contributionMargin, unitPrice) {
  const ctx = document.getElementById('beDonutChart');
  if (!ctx) return;

  if (beDonutChart) beDonutChart.destroy();

  beDonutChart = new Chart(ctx, {
    type: 'doughnut',
    data: {
      labels: ['Variable Cost per Unit', 'Unit Contribution Margin'],
      datasets: [
        {
          data: [variableCost, contributionMargin],
          backgroundColor: [cssVar('--chart-rent'), cssVar('--chart-buy')],
          borderColor: cssVar('--bg-card'),
          borderWidth: 3,
          hoverOffset: 4,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: { color: cssVar('--chart-tick'), usePointStyle: true, pointStyle: 'circle', padding: 16 },
        },
        tooltip: {
          backgroundColor: cssVar('--tooltip-bg'),
          borderColor: cssVar('--tooltip-border'),
          borderWidth: 1,
          titleColor: cssVar('--tooltip-title'),
          bodyColor: cssVar('--tooltip-body'),
          callbacks: {
            label: (ctx) => `${ctx.label}: ${fmt(ctx.raw)} (${((ctx.raw / unitPrice) * 100).toFixed(1)}%)`,
          },
        },
      },
      cutout: '72%',
    },
  });
}

function renderGrowthChart(schedule) {
  const ctx = document.getElementById('beGrowthChart');
  if (!ctx) return;

  const labels = schedule.map((r) => `Mo ${r.month}`);
  const revData = schedule.map((r) => r.totalRevenue);
  const totalCostData = schedule.map((r) => r.totalCosts);
  const baseIncomeData = schedule.map((r) => r.baseIncome);

  if (beGrowthChart) beGrowthChart.destroy();

  beGrowthChart = new Chart(ctx, {
    type: 'line',
    data: {
      labels,
      datasets: [
        {
          label: 'Total Revenue (Base + Unit Sales)',
          data: revData,
          borderColor: cssVar('--chart-buy'),
          backgroundColor: 'rgba(52, 211, 153, 0.08)',
          fill: true,
          tension: 0.2,
          pointRadius: 4,
          borderWidth: 2.5,
        },
        {
          label: 'Total Monthly Costs',
          data: totalCostData,
          borderColor: cssVar('--chart-rent'),
          backgroundColor: 'rgba(239, 68, 68, 0.08)',
          fill: false,
          tension: 0.1,
          pointRadius: 4,
          borderWidth: 2.5,
        },
        {
          label: 'Growing Base Recurring Income',
          data: baseIncomeData,
          borderColor: '#8B5CF6',
          borderDash: [6, 6],
          fill: false,
          pointRadius: 0,
          borderWidth: 2,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: {
          position: 'top',
          align: 'end',
          labels: { color: cssVar('--chart-tick'), usePointStyle: true, pointStyle: 'circle', padding: 16 },
        },
        tooltip: {
          backgroundColor: cssVar('--tooltip-bg'),
          borderColor: cssVar('--tooltip-border'),
          borderWidth: 1,
          titleColor: cssVar('--tooltip-title'),
          bodyColor: cssVar('--tooltip-body'),
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: ${fmt(ctx.raw)}`,
          },
        },
      },
      scales: {
        x: {
          grid: { color: cssVar('--chart-grid') },
          ticks: { color: cssVar('--chart-tick') },
        },
        y: {
          grid: { color: cssVar('--chart-grid') },
          ticks: {
            color: cssVar('--chart-tick'),
            callback: (v) => fmtCompact(v),
          },
        },
      },
    },
  });
}

function renderTable(schedule, beMonth) {
  const tbody = document.getElementById('beScheduleBody');
  if (!tbody) return;

  tbody.innerHTML = schedule.map((row) => {
    const isBep = row.month === beMonth;
    const isProfit = row.profit >= 0;

    return `
      <tr ${isBep ? 'style="background: var(--bg-surface); font-weight: 600;"' : ''}>
        <td>${row.month} ${isBep ? '⭐' : ''}</td>
        <td>${fmt(row.baseIncome)}</td>
        <td>${fmt(row.unitRevenue)}</td>
        <td class="advantage-buy">${fmt(row.totalRevenue)}</td>
        <td>${fmt(row.fixedCosts)}</td>
        <td>${fmt(row.variableCosts)}</td>
        <td>${fmt(row.totalCosts)}</td>
        <td class="${isProfit ? 'advantage-buy' : 'advantage-renter'}">${isProfit ? '+' : ''}${fmt(row.profit)}</td>
      </tr>
    `;
  }).join('');
}

function exportBeCsv() {
  const params = getBeInputs();
  const result = calculateBe(params);
  const headers = ['Month', 'Base Income', 'Unit Sales', 'Revenue', 'Fixed Cost', 'Variable Cost', 'Total Cost', 'Net Profit'];
  const rows = result.schedule.map((r) => [
    r.month, r.baseIncome, r.unitRevenue, r.totalRevenue, r.fixedCosts, r.variableCosts, r.totalCosts, r.profit
  ]);

  const csv = [headers, ...rows].map((r) => r.join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `break-even-12month-recurring-projection.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

function applyBeCountryRanges(country, overrideParams = {}) {
  if (!country) return;
  const curr = country.currency || 'USD';

  let defaultFixed = 500000;

  if (curr === 'INR') {
    defaultFixed = 500000;
  } else if (curr === 'JPY') {
    defaultFixed = 5000000;
  }

  const fInput = document.getElementById('beFixed');
  if (fInput) {
    fInput.value = overrideParams.fixed ?? defaultFixed;
  }

  const pInput = document.getElementById('bePrice');
  if (pInput && overrideParams.price !== null) {
    pInput.value = overrideParams.price;
  }

  const vInput = document.getElementById('beVariable');
  if (vInput && overrideParams.variable !== null) {
    vInput.value = overrideParams.variable;
  }

  const uInput = document.getElementById('beExpectedUnits');
  if (uInput && overrideParams.expectedUnits !== null) {
    uInput.value = overrideParams.expectedUnits;
  }

  const rInput = document.getElementById('beRecurringIncome');
  if (rInput && overrideParams.recurringIncome !== null) {
    rInput.value = overrideParams.recurringIncome;
  }

  const gInput = document.getElementById('beGrowthRate');
  if (gInput && overrideParams.growthRate !== null) {
    gInput.value = overrideParams.growthRate;
  }
}

function recalculateBe() {
  const params = getBeInputs();
  updateDisplays(params);
  updateBeUrlParams(params);
  const result = calculateBe(params);
  renderBeResults(result, params);
}

function initBeCalculator() {
  if (!document.getElementById('beFixed')) return;

  const urlParams = parseBeUrlParams();

  initCountry((country) => {
    applyBeCountryRanges(country, urlParams);
    recalculateBe();
  });

  const inputs = ['beFixed', 'bePrice', 'beVariable', 'beExpectedUnits', 'beRecurringIncome', 'beGrowthRate'];
  inputs.forEach((id) => {
    const el = document.getElementById(id);
    if (el) {
      el.addEventListener('input', recalculateBe);
      el.addEventListener('change', recalculateBe);
    }
  });

  const exportBtn = document.getElementById('exportBeCsv');
  if (exportBtn) {
    exportBtn.addEventListener('click', exportBeCsv);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  initBeCalculator();
});
